import { type FormEvent, useCallback, useMemo, useState } from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  Flex,
  HStack,
  Input,
  Radio,
  RadioGroup,
  Select,
  Switch,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useSnapshot } from 'valtio';

import { EditorMode } from '@duke-hq/shared-constants/assessments';
import { UnitAssessmentFormFieldType } from '@duke-hq/svc-api-db';
import { trpcAdmin } from '../../../lib/trpcAdmin';
import { getFieldPlaceholder } from '@duke-hq/ui/components/AssessmentForm/utils';

import { unitAssessmentState } from '../../UnitAssessmentState';
import type { FormFieldOptionType } from '../FormSlide/types';
import { FormFieldEditorOptions } from './FormFieldEditorOptions';

type FormFieldEditorProps = {
  refetch: () => void;
};

export function FormFieldEditor({ refetch }: FormFieldEditorProps) {
  const { currentSlide, editorMode, currentFormSectionId, currentFormFieldId } =
    useSnapshot(unitAssessmentState);
  const toast = useToast();

  const currentField = useMemo(() => {
    if (editorMode === EditorMode.EDIT && currentSlide !== null && 'sections' in currentSlide) {
      const section = currentSlide.sections?.find(({ id }) => id === currentFormSectionId);
      return section?.fields?.find(({ id }) => id === currentFormFieldId);
    }
  }, [currentSlide, editorMode, currentFormSectionId, currentFormFieldId]);

  const [label, setLabel] = useState(currentField?.label ?? '');
  const [fieldType, setFieldType] = useState<UnitAssessmentFormFieldType>(
    currentField?.fieldType ?? UnitAssessmentFormFieldType.TEXT,
  );
  const [isRequired, setIsRequired] = useState(currentField?.required ?? true);
  const [placeholder, setPlaceholder] = useState(currentField?.placeholder ?? '');
  const [multiline, setMultiline] = useState(currentField?.multiline ?? false);
  const [helperText, setHelperText] = useState(currentField?.helperText ?? '');
  const [options, setOptions] = useState<FormFieldOptionType[]>(
    currentField?.options ? [...currentField.options] : [],
  );
  const [fullWidthLayout, setFullWidthLayout] = useState(currentField?.fullWidthLayout ?? false);

  const isSelectionField =
    fieldType === UnitAssessmentFormFieldType.RADIO ||
    fieldType === UnitAssessmentFormFieldType.SELECT;

  const createFormFieldMutation = trpcAdmin.unitAssessment.createFormField.useMutation({
    onSuccess: () => {
      unitAssessmentState.closeEditor();
      unitAssessmentState.setHasUnpublishedChanges(true);
      refetch();
      toast({ title: t`Field created successfully`, status: 'success' });
    },
    onError: (error) => {
      toast({ title: error.message, status: 'error' });
    },
  });

  const updateFormFieldMutation = trpcAdmin.unitAssessment.updateFormField.useMutation({
    onSuccess: () => {
      unitAssessmentState.closeEditor();
      unitAssessmentState.setHasUnpublishedChanges(true);
      refetch();
      toast({ title: t`Field updated successfully`, status: 'success' });
    },
    onError: (error) => {
      toast({ title: error.message, status: 'error' });
    },
  });

  const handleSave = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const payload = {
        label,
        fieldType,
        required: isRequired,
        placeholder,
        multiline,
        helperText,
        fullWidthLayout,
      };

      if (editorMode === EditorMode.CREATE && currentFormSectionId !== null) {
        await createFormFieldMutation.mutateAsync({
          ...payload,
          sectionId: currentFormSectionId,
          ...(isSelectionField ? { options } : {}),
        });
      } else if (editorMode === EditorMode.EDIT && currentFormFieldId !== null) {
        await updateFormFieldMutation.mutateAsync({
          ...payload,
          id: currentFormFieldId,
          ...(isSelectionField ? { options } : {}),
        });
      }
    },
    [
      label,
      fieldType,
      isRequired,
      placeholder,
      multiline,
      helperText,
      fullWidthLayout,
      options,
      createFormFieldMutation,
      updateFormFieldMutation,
      isSelectionField,
      editorMode,
      currentFormSectionId,
      currentFormFieldId,
    ],
  );

  const handleFieldTypeChange = useCallback(
    (newFieldType: UnitAssessmentFormFieldType) => {
      setFieldType(newFieldType);

      // non-TEXT fields are always required for now
      if (newFieldType !== UnitAssessmentFormFieldType.TEXT) {
        setIsRequired(true);
      }

      // These are always full width
      if (
        newFieldType === UnitAssessmentFormFieldType.RADIO ||
        (UnitAssessmentFormFieldType.TEXT && multiline)
      ) {
        setFullWidthLayout(true);
      }
    },
    [multiline],
  );

  return (
    <form onSubmit={handleSave}>
      <VStack spacing={4} align="stretch">
        <FormControl isRequired>
          <FormLabel htmlFor="field-label">
            <Trans>Field Label</Trans>
          </FormLabel>
          <Input
            id="field-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={t`Enter the field label`}
          />
        </FormControl>

        <FormControl isRequired>
          <FormLabel htmlFor="field-type">
            <Trans>Field Type</Trans>
          </FormLabel>
          <Select
            id="field-type"
            value={fieldType}
            // Option values are only of UnitAssessmentFormFieldType
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            onChange={(e) => handleFieldTypeChange(e.target.value as UnitAssessmentFormFieldType)}
          >
            <option value={UnitAssessmentFormFieldType.TEXT}>
              <Trans>Text</Trans>
            </option>
            <option value={UnitAssessmentFormFieldType.RADIO}>
              <Trans>Radio</Trans>
            </option>
            <option value={UnitAssessmentFormFieldType.SELECT}>
              <Trans>Select</Trans>
            </option>
          </Select>
        </FormControl>

        {fieldType === UnitAssessmentFormFieldType.TEXT ? (
          <FormControl>
            <RadioGroup
              aria-label={t`Text field type`}
              onChange={(value) => {
                const isMultiline = value === 'multiline';
                setMultiline(isMultiline);
                if (isMultiline) {
                  setFullWidthLayout(true);
                }
              }}
              value={multiline ? 'multiline' : 'singleline'}
            >
              <HStack spacing={6}>
                <Radio value="singleline" colorScheme="purple">
                  <Trans>Single-line</Trans>
                </Radio>
                <Radio value="multiline" colorScheme="purple">
                  <Trans>Multi-line</Trans>
                </Radio>
              </HStack>
            </RadioGroup>
          </FormControl>
        ) : null}

        {fieldType === UnitAssessmentFormFieldType.TEXT ||
        fieldType === UnitAssessmentFormFieldType.SELECT ? (
          <FormControl>
            <FormLabel htmlFor="placeholder">
              <Trans>Placeholder</Trans>
            </FormLabel>
            <Input
              id="placeholder"
              value={placeholder}
              onChange={(e) => setPlaceholder(e.target.value)}
              placeholder={getFieldPlaceholder(fieldType, label, placeholder)}
            />
          </FormControl>
        ) : null}

        {isSelectionField === true && (
          <FormFieldEditorOptions options={options} setOptions={setOptions} fieldType={fieldType} />
        )}

        <FormControl>
          <FormLabel htmlFor="helper-text">
            <Trans>Helper Text</Trans>
          </FormLabel>
          <Input
            id="helper-text"
            value={helperText}
            onChange={(e) => setHelperText(e.target.value)}
            placeholder={t`Enter helper text`}
          />
        </FormControl>

        {fieldType === UnitAssessmentFormFieldType.TEXT ? (
          <FormControl display="flex" alignItems="center">
            <FormLabel htmlFor="is-required" mb="0">
              <Switch
                id="is-required"
                isChecked={isRequired}
                mr={2}
                onChange={(e) => setIsRequired(e.target.checked)}
              />
              <Trans>Required field</Trans>
            </FormLabel>
          </FormControl>
        ) : null}

        {fieldType === UnitAssessmentFormFieldType.SELECT ||
        (fieldType === UnitAssessmentFormFieldType.TEXT && !multiline) ? (
          <FormControl display="flex" alignItems="center">
            <FormLabel htmlFor="full-width-layout" mb="0">
              <Switch
                id="full-width-layout"
                isChecked={fullWidthLayout}
                mr={2}
                onChange={(e) => setFullWidthLayout(e.target.checked)}
              />
              <Trans>Always full width</Trans>
            </FormLabel>
          </FormControl>
        ) : null}

        <Flex justify="flex-end">
          <Button right={0} type="submit">
            {editorMode === EditorMode.CREATE ? <Trans>Add</Trans> : <Trans>Update</Trans>}
          </Button>
        </Flex>
      </VStack>
    </form>
  );
}
